/**
 * Which controls to show (docs/fight.md §9, docs/architecture.md §6): touch controls on a
 * coarse pointer or after the first touch, key hints on a fine pointer or after the first key
 * press. A device with both (touch laptop, tablet with a keyboard) shows both.
 *
 * Dev overrides: `?touch` forces the touch controls, `?keys` forces the key hints.
 */
import { useSyncExternalStore } from 'react';
import { isTypingTarget } from './bindings';

export interface DeviceMode {
  /** Show the joystick and the round touch buttons. */
  touch: boolean;
  /** Show key hints (and, without touch, the keyboard layout of the controls). */
  keys: boolean;
}

const params = new URLSearchParams(typeof location === 'undefined' ? '' : location.search);
const FORCE_TOUCH = params.has('touch');
const FORCE_KEYS = params.has('keys');

let touchSeen = false;
let keysSeen = false;
let installed = false;
const listeners = new Set<() => void>();

function matches(query: string): boolean {
  return typeof matchMedia === 'function' && matchMedia(query).matches;
}

function compute(): DeviceMode {
  const touch = FORCE_TOUCH || touchSeen || matches('(pointer: coarse)');
  // With no pointer information at all, assume a keyboard.
  const keys = FORCE_KEYS || keysSeen || matches('(pointer: fine)') || !touch;
  return { touch, keys };
}

let mode: DeviceMode = compute();

function update(): void {
  const next = compute();
  if (next.touch === mode.touch && next.keys === mode.keys) return;
  mode = next;
  for (const l of listeners) l();
}

/** A touch landed somewhere: touch controls from now on. */
export function noteTouch(): void {
  if (touchSeen) return;
  touchSeen = true;
  update();
}

/** A key was pressed: key hints from now on. */
export function noteKeyboard(): void {
  if (keysSeen) return;
  keysSeen = true;
  update();
}

function install(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  for (const q of ['(pointer: coarse)', '(pointer: fine)']) {
    if (typeof matchMedia === 'function') matchMedia(q).addEventListener('change', update);
  }
  const opts = { capture: true, passive: true } as const;
  window.addEventListener('pointerdown', (e) => e.pointerType === 'touch' && noteTouch(), opts);
  window.addEventListener('keydown', (e) => !isTypingTarget(e.target) && noteKeyboard(), opts);
}

export function getDeviceMode(): DeviceMode {
  install();
  return mode;
}

export function subscribeDeviceMode(cb: () => void): () => void {
  install();
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useDeviceMode(): DeviceMode {
  return useSyncExternalStore(subscribeDeviceMode, getDeviceMode);
}

// Listen from app start, so a touch or key press in the menus already counts.
install();
